import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Store, Clapperboard, ShieldAlert, Radio } from "lucide-react";

const SUB = [
  { id: "vendors", label: "Vendors", icon: Store },
  { id: "creators", label: "Creators", icon: Clapperboard },
  { id: "official_tv", label: "Ponderr Channel Push", icon: Radio },
  { id: "moderation", label: "Moderation", icon: ShieldAlert },
];

function Vendors() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/global-admin/vendors").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const verify = async (id) => { try { await api.patch(`/global-admin/vendors/${id}/verify`); toast.success("Vendor verified"); load(); } catch { toast.error("Failed"); } };
  return (
    <div className="nb-card p-0 overflow-hidden">
      <table className="nb-table">
        <thead><tr><th>Business</th><th>Contact</th><th>Location</th><th>KYC</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {rows.map(v => (
            <tr key={v.vendor_id} data-testid={`ga-vendor-${v.vendor_id}`}>
              <td className="font-bold">{v.business_name}</td><td className="text-xs">{v.contact_name}<div className="mono opacity-60">{v.email}</div></td>
              <td className="text-xs">{v.city} {v.postal_code}</td>
              <td><span className={`nb-badge ${v.kyc_status === "VERIFIED" ? "bg-[#86EFAC]" : "bg-[#FDE047]"}`}>{v.kyc_status}</span></td>
              <td className="text-xs">{v.status}</td>
              <td>{v.kyc_status !== "VERIFIED" && <button className="nb-btn text-xs" onClick={() => verify(v.vendor_id)} data-testid={`ga-vendor-verify-${v.vendor_id}`}>Verify KYC</button>}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No vendors yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Creators() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/global-admin/creators").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggleKids = async (c) => { try { await api.patch(`/global-admin/creators/${c.creator_id}/kids-approved`, { is_kids_approved: !c.is_kids_approved }); toast.success(`Kids-approved ${!c.is_kids_approved ? "ON" : "OFF"}`); load(); } catch { toast.error("Failed"); } };
  const toggleAccess = async (c, key) => { try { await api.patch(`/global-admin/creators/${c.creator_id}/access`, { [key]: !c[key] }); toast.success("Updated"); load(); } catch { toast.error("Failed"); } };
  const setSplit = async (c, pct) => { const v = parseInt(pct); if (isNaN(v)) return; try { await api.patch(`/global-admin/creators/${c.creator_id}/access`, { revenue_split_educator_pct: v }); toast.success(`Split ${v}/${100 - v}`); load(); } catch { toast.error("Failed"); } };
  const verify = async (id) => { try { await api.patch(`/global-admin/creators/${id}/verify`); toast.success("Creator verified"); load(); } catch { toast.error("Failed"); } };
  const bool = (c, k) => c[k] !== false; // default true when undefined
  return (
    <div className="nb-card p-0 overflow-hidden">
      <table className="nb-table">
        <thead><tr><th>Creator</th><th>Tier</th><th>Course</th><th>TV</th><th>Kids OK</th><th>Split %</th><th>Status</th></tr></thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.creator_id} data-testid={`ga-creator-${c.creator_id}`}>
              <td className="font-bold">{c.display_name}<div className="text-xs mono opacity-60">{c.email}</div></td>
              <td className="mono text-xs">{c.tier_key}</td>
              <td><button className={`nb-btn text-xs ${bool(c, "has_course_creator_access") ? "" : "nb-btn-ghost"}`} onClick={() => toggleAccess(c, "has_course_creator_access")} data-testid={`ga-creator-course-${c.creator_id}`}>{bool(c, "has_course_creator_access") ? "ON" : "OFF"}</button></td>
              <td><button className={`nb-btn text-xs ${bool(c, "has_tv_channel_access") ? "" : "nb-btn-ghost"}`} onClick={() => toggleAccess(c, "has_tv_channel_access")} data-testid={`ga-creator-tv-${c.creator_id}`}>{bool(c, "has_tv_channel_access") ? "ON" : "OFF"}</button></td>
              <td><button className={`nb-btn text-xs ${c.is_kids_approved ? "" : "nb-btn-ghost"}`} onClick={() => toggleKids(c)} data-testid={`ga-creator-kids-${c.creator_id}`}>{c.is_kids_approved ? "ON" : "OFF"}</button></td>
              <td><input className="nb-input mono w-16 text-xs" defaultValue={c.revenue_split_educator_pct ?? 70} onBlur={e => setSplit(c, e.target.value)} data-testid={`ga-creator-split-${c.creator_id}`} /></td>
              <td>{c.status !== "ACTIVE" ? <button className="nb-btn text-xs" onClick={() => verify(c.creator_id)} data-testid={`ga-creator-verify-${c.creator_id}`}>Verify</button> : <span className="nb-badge bg-[#86EFAC] text-xs">ACTIVE</span>}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-500">No creators yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Moderation() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/global-admin/moderation/queue").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const approve = async (id) => { try { await api.post(`/global-admin/moderation/${id}/approve`); toast.success("Approved & published"); load(); } catch { toast.error("Failed"); } };
  const reject = async (id) => { const reason = window.prompt("Reason for rejection:"); if (reason === null) return; try { await api.post(`/global-admin/moderation/${id}/reject`, { reason }); toast.success("Rejected"); load(); } catch { toast.error("Failed"); } };
  return (
    <div className="nb-card p-0 overflow-hidden">
      <table className="nb-table">
        <thead><tr><th>Title</th><th>Creator</th><th>Curriculum</th><th>AI Score</th><th>Flags</th><th>Action</th></tr></thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.content_id} data-testid={`ga-mod-${c.content_id}`}>
              <td className="font-bold">{c.title}</td><td className="text-xs">{c.creator_name}</td>
              <td className="text-xs">{c.board} {c.grade} · {c.subject}</td>
              <td className="mono text-xs">{c.moderation?.composite_score ?? "—"} <span className="opacity-50">nsfw {c.moderation?.nsfw_score ?? "—"}</span></td>
              <td className="text-xs">{(c.moderation?.flags || []).join(", ") || "—"}</td>
              <td className="flex gap-1"><button className="nb-btn text-xs" onClick={() => approve(c.content_id)} data-testid={`ga-mod-approve-${c.content_id}`}>Approve</button>
                <button className="nb-btn nb-btn-danger text-xs" onClick={() => reject(c.content_id)} data-testid={`ga-mod-reject-${c.content_id}`}>Reject</button></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">Moderation queue is clear ✨</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function OfficialTVPush() {
  const [boards, setBoards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ title: "", external_url: "", description: "", board: "", grade: "", subject: "", thumbnail_url: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/global/boards").then(r => setBoards(r.data)).catch(() => {});
    api.get("/global/master-grades").then(r => setGrades(r.data)).catch(() => {});
    api.get("/global-admin/tv/official").then(r => setRows(r.data)).catch(() => {});
  }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const publish = async () => {
    if (!f.title || !f.external_url) { toast.error("Title and video URL are required"); return; }
    setBusy(true);
    try {
      await api.post("/global-admin/tv/publish-official", f);
      toast.success(`Published to Ponderr Channel${f.grade ? ` · fanning out to ${f.grade}` : ""}`);
      setF({ title: "", external_url: "", description: "", board: "", grade: "", subject: "", thumbnail_url: "" });
      api.get("/global-admin/tv/official").then(r => setRows(r.data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="nb-card p-5 bg-[#C4B5FD] space-y-2 h-fit">
        <h3 className="display text-lg font-bold flex items-center gap-2"><Radio size={18} /> Push Official Video</h3>
        <p className="text-xs">Publishes straight to the student <b>Ponderr Channel</b> tab and fans out to every matching grade.</p>
        <input className="nb-input" placeholder="Title *" value={f.title} onChange={set("title")} data-testid="official-title" />
        <input className="nb-input" placeholder="YouTube / video URL *" value={f.external_url} onChange={set("external_url")} data-testid="official-url" />
        <textarea className="nb-input" rows="2" placeholder="Description" value={f.description} onChange={set("description")} data-testid="official-desc" />
        <div className="grid grid-cols-2 gap-2">
          <select className="nb-select text-sm" value={f.board} onChange={set("board")} data-testid="official-board">
            <option value="">Board</option>
            {boards.map(b => <option key={b.board_id} value={b.code}>{b.code}</option>)}
          </select>
          <select className="nb-select text-sm" value={f.grade} onChange={set("grade")} data-testid="official-grade">
            <option value="">All grades</option>
            {grades.map(g => <option key={g.code} value={g.code}>{g.code}</option>)}
          </select>
          <input className="nb-input text-sm" placeholder="Subject" value={f.subject} onChange={set("subject")} data-testid="official-subject" />
          <input className="nb-input text-sm" placeholder="Thumbnail URL" value={f.thumbnail_url} onChange={set("thumbnail_url")} data-testid="official-thumb" />
        </div>
        <button className="nb-btn w-full justify-center" onClick={publish} disabled={busy} data-testid="official-publish">
          {busy ? "Publishing…" : "Publish to Ponderr Channel"}
        </button>
      </div>
      <div className="nb-card p-0 overflow-hidden lg:col-span-2">
        <table className="nb-table">
          <thead><tr><th>Title</th><th>Curriculum</th><th>Status</th></tr></thead>
          <tbody>
            {rows.map(c => (
              <tr key={c.content_id} data-testid={`official-row-${c.content_id}`}>
                <td className="font-bold">{c.title}</td>
                <td className="text-xs">{c.board} {c.grade || "All"} · {c.subject}</td>
                <td><span className="nb-badge bg-[#86EFAC]">{c.status}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={3} className="text-center py-6 text-slate-500">No official videos pushed yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function GlobalMarketplace() {
  const [sub, setSub] = useState("vendors");
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {SUB.map(s => {
          const Icon = s.icon;
          return <button key={s.id} onClick={() => setSub(s.id)} data-testid={`ga-mp-tab-${s.id}`}
            className={`nb-btn text-xs ${sub === s.id ? "" : "nb-btn-ghost"}`}><Icon size={14} /> {s.label}</button>;
        })}
      </div>
      {sub === "vendors" && <Vendors />}
      {sub === "creators" && <Creators />}
      {sub === "official_tv" && <OfficialTVPush />}
      {sub === "moderation" && <Moderation />}
    </div>
  );
}
